import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import {
  attachSymbol,
  CABLE_TYPES,
  distributeSymbols,
  insideSide,
  magnetSnap,
  newId,
  openingEdges,
  planBounds,
  setOpeningLeft,
  wallHeight,
  wallLength,
  wallViewFlipped,
  zoneHeights,
  formatMeters,
  OPENING_DEFAULTS,
  OPENING_KINDS,
  PLAN_SCALES,
  PLAN_SHEETS,
  type PlanSheet,
  parseLength,
  removeRef,
  SYMBOL_CATEGORIES,
  symbolDef,
  symbolK,
  symbolSize,
  SYMBOLS,
  WALL_PRESETS,
  wallFrame,
  type PlanData,
  type Prim,
  type RoomBasis,
} from "@handvero/shared";
import { Message, Sheet } from "./ui";

/*
 * Wandansicht: Eine Wand von der Raumseite gesehen, mit Raumhöhe, Installationszonen, Fenstern und Türen.
 * Hier kommen Steckdosen, Schalter und andere Teile mit Höhe und Abstand an die Wand. Im Grundriss stehen sie
 * danach automatisch an der richtigen Stelle der Wand.
 */

const PAPER = "#ffffff";
const INK = "#1b1d22";
const HILITE = "#2f6fd0";
const FONT = "Roboto, Segoe UI, Arial, sans-serif";

function Glyph({ prims, sw = 1.3 }: { prims: Prim[]; sw?: number }) {
  return (
    <>
      {prims.map((p, i) => {
        if (p.t === "l") return <line key={i} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={INK} strokeWidth={sw} strokeLinecap="round" />;
        if (p.t === "c") return <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={p.f ? INK : PAPER} stroke={INK} strokeWidth={sw} />;
        if (p.t === "r") return <rect key={i} x={p.x} y={p.y} width={p.w} height={p.h} fill={p.f ? INK : PAPER} stroke={INK} strokeWidth={sw} />;
        if (p.t === "p") {
          const d = p.pts.map((q, j) => `${j ? "L" : "M"}${q[0]} ${q[1]}`).join(" ") + (p.closed || p.f ? " Z" : "");
          return <path key={i} d={d} fill={p.f ? INK : "none"} stroke={INK} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" />;
        }
        return (
          <text key={i} x={p.x} y={p.y} fontSize={p.size} fontWeight={700} textAnchor="middle" dominantBaseline="central" fill={INK} fontFamily={FONT}>
            {p.s}
          </text>
        );
      })}
    </>
  );
}

function SymbolIcon({ id, size = 28 }: { id: string; size?: number }) {
  const def = symbolDef(id);
  if (!def) return null;
  return (
    <svg width={size} height={size} viewBox="-11 -11 22 22" aria-hidden="true">
      <rect x="-11" y="-11" width="22" height="22" fill={PAPER} />
      <Glyph prims={def.prims} />
    </svg>
  );
}

const fmtCm = (v: number) => (Math.round(v * 10) / 10).toString().replace(".", ",");

function Field(props: { label: string; value: number; min?: number; max?: number; onCommit: (v: number) => void; setMsg: (m: string) => void }) {
  const { min = 0, max = 100000 } = props;
  const text = fmtCm(props.value);
  return (
    <label>
      {props.label}
      <input
        key={text}
        defaultValue={text}
        inputMode="decimal"
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          const v = raw === "0" ? 0 : parseLength(`${raw}cm`);
          if (v === null || v < min || v > max) return props.setMsg(`Bitte eine Zahl zwischen ${min} und ${max} cm eingeben.`);
          props.setMsg("");
          if (Math.abs(v - props.value) > 0.0001) props.onCommit(v);
        }}
      />
    </label>
  );
}

/* ---------- Wandansicht ---------- */

type Tool = "symbol" | "cable" | "select";
type Sel = { k: "symbol" | "opening" | "cable"; id: string } | null;
type Pt = [number, number];

/** Verständlicher Name einer Wand: „Wohnzimmer · obere Wand“. */
function wallTitle(data: PlanData, wallId: string): string {
  const w = data.walls.find((x) => x.id === wallId);
  if (!w) return "";
  const room = data.rooms.find((r) => r.wallIds?.includes(wallId));
  let side = `Wand ${data.walls.findIndex((x) => x.id === wallId) + 1}`;
  if (room) {
    const mx = (w.x1 + w.x2) / 2;
    const my = (w.y1 + w.y2) / 2;
    if (Math.abs(w.y1 - w.y2) < 1) side = my < room.y + room.h / 2 ? "obere Wand" : "untere Wand";
    else if (Math.abs(w.x1 - w.x2) < 1) side = mx < room.x + room.w / 2 ? "linke Wand" : "rechte Wand";
  }
  return `${room ? `${room.name || "Raum"} · ` : ""}${side}`;
}

/** Kleiner Grundriss zum Wechseln der Wand: die gewählte Wand ist blau, der Pfeil zeigt die Blickrichtung. */
function MiniPlan({ data, wallId, onPick }: { data: PlanData; wallId: string; onPick: (id: string) => void }) {
  const b = planBounds(data);
  if (!b) return null;
  const pad = 40;
  const w = b.maxX - b.minX + 2 * pad;
  const h = b.maxY - b.minY + 2 * pad;
  const cur = data.walls.find((x) => x.id === wallId);
  const arrow = (() => {
    if (!cur) return null;
    const len = wallLength(cur);
    if (len < 1) return null;
    const side = insideSide(data, cur);
    const nx = (-(cur.y2 - cur.y1) / len) * side;
    const ny = ((cur.x2 - cur.x1) / len) * side;
    const mx = (cur.x1 + cur.x2) / 2;
    const my = (cur.y1 + cur.y2) / 2;
    const L = Math.max(w, h) * 0.09;
    // Pfeil zeigt von innen zur Wand (so schaut man in der Wandansicht).
    return { x1: mx + nx * L * 1.6, y1: my + ny * L * 1.6, x2: mx + nx * L * 0.25, y2: my + ny * L * 0.25, L };
  })();
  const sw = Math.max(w, h) / 90;
  return (
    <svg className="plan-mini" viewBox={`${b.minX - pad} ${b.minY - pad} ${w} ${h}`} role="group" aria-label="Wand wählen">
      <defs>
        <marker id="wv-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0 0 L10 5 L0 10 z" fill={HILITE} />
        </marker>
      </defs>
      {data.walls.map((x) => (
        <g key={x.id} onClick={() => onPick(x.id)} style={{ cursor: "pointer" }}>
          <title>{wallTitle(data, x.id)}</title>
          <line x1={x.x1} y1={x.y1} x2={x.x2} y2={x.y2} stroke={x.id === wallId ? HILITE : "#8f929a"} strokeWidth={x.id === wallId ? sw * 2.2 : sw * 1.4} strokeLinecap="round" />
          <line x1={x.x1} y1={x.y1} x2={x.x2} y2={x.y2} stroke="transparent" strokeWidth={sw * 6} />
        </g>
      ))}
      {arrow && <line x1={arrow.x1} y1={arrow.y1} x2={arrow.x2} y2={arrow.y2} stroke={HILITE} strokeWidth={sw * 1.2} markerEnd="url(#wv-arrow)" pointerEvents="none" />}
    </svg>
  );
}

const distToSeg = (px: number, py: number, a: Pt, b: Pt) => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / l2));
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
};

export function WallView(props: { data: PlanData; wallId: string; onCommit: (next: PlanData) => void; onClose: () => void; onSelectWall: (id: string) => void }) {
  const { data, wallId, onCommit, onClose, onSelectWall } = props;
  const frame = wallFrame(data, wallId);
  const svgRef = useRef<SVGSVGElement>(null);
  const [category, setCategory] = useState(SYMBOL_CATEGORIES[0]!);
  const [tool, setTool] = useState<Tool>("symbol");
  const [placing, setPlacing] = useState<string>("steckdose");
  const [cableType, setCableType] = useState<string>(CABLE_TYPES[0]);
  const [draft, setDraft] = useState<Pt[]>([]);
  const [sel, setSel] = useState<Sel>(null);
  const [msg, setMsg] = useState("");
  const [cursor, setCursor] = useState<{ x: number; h: number } | null>(null);
  const [drag, setDrag] = useState<{ k: "symbol" | "opening"; id: string; x: number; h: number } | null>(null);
  const dragRef = useRef<{ k: "symbol" | "opening"; id: string; dx: number; dh: number } | null>(null);
  const [showZones, setShowZones] = useState(() => {
    try {
      return localStorage.getItem("hv-plan-zones") !== "0";
    } catch {
      return true;
    }
  });
  const toggleZones = (v: boolean) => {
    setShowZones(v);
    try {
      localStorage.setItem("hv-plan-zones", v ? "1" : "0");
    } catch {
      /* ohne Speicher: nur für diese Ansicht */
    }
  };
  const [series, setSeries] = useState({ type: "steckdose", count: 3, h: 30, margin: 30 });

  const inner = frame?.inner ?? 0;
  const a0 = frame?.a0 ?? 0;
  const H = wallHeight(data, wallId);
  const flip = frame ? wallViewFlipped(data, frame.wall) : false;
  /** Zwischen Zeichenrichtung der Wand und Blick von innen umrechnen (gilt in beide Richtungen). */
  const fx = (p: number) => (flip ? inner - p : p);
  const zones = useMemo(() => zoneHeights(data, H), [data, H]);
  const room = data.rooms.find((r) => r.wallIds?.includes(wallId));

  const mine = data.symbols.filter((s) => s.wallId === wallId && s.pos !== undefined);
  const openings = data.openings.filter((o) => o.wallId === wallId);
  const cables = data.cables.filter((c) => c.wallId === wallId);
  const selSymbol = sel?.k === "symbol" ? mine.find((s) => s.id === sel.id) : undefined;
  const selOpening = sel?.k === "opening" ? openings.find((o) => o.id === sel.id) : undefined;
  const selCable = sel?.k === "cable" ? cables.find((c) => c.id === sel.id) : undefined;
  const edges = selOpening ? openingEdges(data, selOpening) : null;

  /** Senkrechte Zonen (Anschauungswerte): neben Ecken und Öffnungen, 15 cm breit. */
  const vzones = useMemo(() => {
    const list: Pt[] = [
      [0, 15],
      [inner - 15, inner],
    ];
    for (const o of openings) {
      const e = openingEdges(data, o);
      if (!e) continue;
      list.push([Math.max(0, e.left - 15), e.left], [Math.min(inner, e.left + o.w), Math.min(inner, e.left + o.w + 15)]);
    }
    return list;
  }, [data, openings, inner]);

  const cancelDraft = () => setDraft([]);
  const finishCable = () => {
    if (draft.length < 2) return setDraft([]);
    onCommit({ ...data, cables: [...data.cables, { id: newId(), wallId, type: cableType, pts: draft.map(([x, h]) => [Math.max(0, fx(x) + a0), h] as Pt) }] });
    setDraft([]);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA")) return;
      if (e.key === "Escape") {
        if (draft.length) cancelDraft();
        else if (sel) setSel(null);
        else if (tool !== "select") setTool("select");
        else onClose();
      } else if (e.key === "Enter" && draft.length) {
        finishCable();
      } else if ((e.key === "Delete" || e.key === "Backspace") && sel && sel.k !== "opening") {
        removeSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!frame) return null;

  function removeSelected() {
    if (!sel) return;
    if (sel.k === "symbol") onCommit(removeRef(data, { kind: "symbol", id: sel.id }));
    else if (sel.k === "cable") onCommit({ ...data, cables: data.cables.filter((c) => c.id !== sel.id) });
    setSel(null);
  }

  const raw = (e: { clientX: number; clientY: number }) => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: p.x, h: -p.y };
  };
  const clampX = (x: number) => Math.min(inner, Math.max(0, x));
  const snapH = (h: number, withSymbols = false) => {
    const cands = [...zones.map((z) => z.h), ...(withSymbols ? mine.map((s) => s.h ?? 30) : [])];
    const near = cands.find((v) => Math.abs(v - h) <= 8);
    return Math.min(H, Math.max(0, near ?? Math.round(h / 5) * 5));
  };
  const snapX = (x: number, strong = false) => {
    const cands = [...mine.map((s) => fx(s.pos! - a0)), ...(strong ? vzones.map((z) => (z[0] + z[1]) / 2) : [])];
    const near = cands.find((v) => Math.abs(v - x) <= 6);
    return clampX(near ?? Math.round(x / 5) * 5);
  };
  const size = (k: number) => symbolSize(data.scale) * k;
  /** Magnet: Symbol-Quadrate berühren sich seitlich oder oben/unten. */
  const magnet = (x: number, h: number, k: number, ignore?: string): [number, number] => {
    const others = mine.filter((s) => s.id !== ignore).map((s) => ({ x: fx(s.pos! - a0), y: s.h ?? 30, half: size(symbolK(s)) / 2 }));
    const [mx, mh] = magnetSnap(x, h, size(k) / 2, others, 8);
    return [clampX(mx), Math.min(H, Math.max(0, mh))];
  };
  const patchSymbol = (id: string, fields: Record<string, unknown>) => onCommit({ ...data, symbols: data.symbols.map((s) => (s.id === id ? { ...s, ...fields } : s)) });
  const patchOpening = (id: string, fields: Record<string, unknown>) => onCommit({ ...data, openings: data.openings.map((o) => (o.id === id ? { ...o, ...fields } : o)) });

  /** Nächster Punkt der Leitung: nur waagerecht oder senkrecht vom letzten Punkt aus. */
  const cablePoint = (r: { x: number; h: number }): Pt => {
    const x = snapX(r.x, true);
    const h = snapH(r.h, true);
    const last = draft[draft.length - 1];
    if (!last) return [x, h];
    return Math.abs(x - last[0]) >= Math.abs(h - last[1]) ? [x, last[1]] : [last[0], h];
  };

  function down(e: ReactPointerEvent<SVGSVGElement>) {
    const r = raw(e);
    if (tool === "cable") {
      const p = cablePoint(r);
      const last = draft[draft.length - 1];
      if (last && last[0] === p[0] && last[1] === p[1]) return;
      setDraft([...draft, p]);
      setSel(null);
      return;
    }
    const hit = [...mine].reverse().find((s) => {
      const half = size(symbolK(s)) * 0.6;
      return Math.abs(r.x - fx(s.pos! - a0)) <= half && Math.abs(r.h - (s.h ?? 30)) <= half;
    });
    if (hit) {
      const sx = fx(hit.pos! - a0);
      setSel({ k: "symbol", id: hit.id });
      dragRef.current = { k: "symbol", id: hit.id, dx: sx - r.x, dh: (hit.h ?? 30) - r.h };
      setDrag({ k: "symbol", id: hit.id, x: sx, h: hit.h ?? 30 });
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    const cab = cables.find((c) => {
      const v = c.pts.map(([x, h]) => [fx(x - a0), h] as Pt);
      return v.some((q, i) => i > 0 && distToSeg(r.x, r.h, v[i - 1]!, q) <= 6);
    });
    if (cab) return setSel({ k: "cable", id: cab.id });
    if (tool === "select") {
      const op = openings.find((o) => {
        const e2 = openingEdges(data, o);
        if (!e2) return false;
        const sill = o.kind === "fenster" ? (o.sill ?? OPENING_DEFAULTS.fenster.sill) : 0;
        const oh = o.h ?? OPENING_DEFAULTS[o.kind].h;
        return r.x >= e2.left && r.x <= e2.left + o.w && r.h >= sill && r.h <= sill + oh;
      });
      if (op) {
        const e2 = openingEdges(data, op)!;
        const cx = e2.left + op.w / 2;
        setSel({ k: "opening", id: op.id });
        dragRef.current = { k: "opening", id: op.id, dx: cx - r.x, dh: 0 };
        setDrag({ k: "opening", id: op.id, x: cx, h: 0 });
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      setSel(null);
      return;
    }
    const [x, hh] = magnet(snapX(r.x), snapH(r.h), 1);
    const placed = attachSymbol(data, wallId, placing, fx(x), hh);
    if (placed) {
      onCommit(placed.data);
      setSel({ k: "symbol", id: placed.id });
    }
  }

  function move(e: ReactPointerEvent<SVGSVGElement>) {
    const r = raw(e);
    if (tool === "cable") {
      const p = cablePoint(r);
      setCursor({ x: p[0], h: p[1] });
    } else {
      const [cx, ch] = tool === "symbol" ? magnet(snapX(r.x), snapH(r.h), 1) : [snapX(r.x), snapH(r.h)];
      setCursor({ x: cx, h: ch });
    }
    const d = dragRef.current;
    if (d) {
      if (d.k === "opening") setDrag({ k: d.k, id: d.id, x: clampX(Math.round((r.x + d.dx) / 5) * 5), h: 0 });
      else {
        const sym = mine.find((s) => s.id === d.id);
        const [x, h] = magnet(snapX(r.x + d.dx), snapH(r.h + d.dh), sym ? symbolK(sym) : 1, d.id);
        setDrag({ k: d.k, id: d.id, x, h });
      }
    }
  }

  function up() {
    if (dragRef.current && drag) {
      if (drag.k === "symbol") patchSymbol(drag.id, { pos: fx(drag.x) + a0, h: drag.h });
      else {
        const o = data.openings.find((x) => x.id === drag.id);
        if (o) onCommit(setOpeningLeft(data, o.id, drag.x - o.w / 2));
      }
    }
    dragRef.current = null;
    setDrag(null);
  }

  function addSeries() {
    const r = distributeSymbols(data, wallId, series.type, series.count, series.h, series.margin);
    if (!r.added) return setMsg("Dafür war keine freie Stelle auf der Wand. Bitte Anzahl, Höhe oder Randabstand ändern.");
    setMsg("");
    onCommit(r.data);
  }

  const wallIdx = data.walls.findIndex((w) => w.id === wallId);
  const step = (dir: 1 | -1) => {
    const n = data.walls.length;
    if (n > 1) onSelectWall(data.walls[(wallIdx + dir + n) % n]!.id);
  };

  const cableLen = (c: { pts: Pt[] }) => c.pts.reduce((sum, p, i) => (i ? sum + Math.hypot(p[0] - c.pts[i - 1]![0], p[1] - c.pts[i - 1]![1]) : 0), 0);

  const pad = 44;
  const vbW = inner + 2 * pad + 130;
  const vbH = H + 2 * pad + 20;
  const dimY = 24;
  const sizeSel = selSymbol ? size(symbolK(selSymbol)) : 0;

  return createPortal(
    <div className="plan-ed plan-wv" role="dialog" aria-label="Wandansicht">
      <div className="plan-ed-top">
        <div className="plan-seg" role="tablist" aria-label="Ansicht wählen">
          <button role="tab" aria-selected={false} onClick={onClose}>
            Grundriss
          </button>
          <button role="tab" aria-selected={true} className="on">
            Wandansicht
          </button>
        </div>
        <button className="dbtn ghost small" onClick={() => step(-1)} aria-label="Vorherige Wand" disabled={data.walls.length < 2}>
          ‹
        </button>
        <select aria-label="Wand wählen" value={wallId} onChange={(e) => onSelectWall(e.target.value)} className="plan-wv-pick">
          {data.walls.map((w) => (
            <option key={w.id} value={w.id}>
              {wallTitle(data, w.id)}
            </option>
          ))}
        </select>
        <button className="dbtn ghost small" onClick={() => step(1)} aria-label="Nächste Wand" disabled={data.walls.length < 2}>
          ›
        </button>
        <b className="plan-wv-title">
          lichte Länge {formatMeters(inner)} · Höhe {formatMeters(H)}
        </b>
        <span className="muted-s">Blick von innen auf die Wand</span>
      </div>
      {msg && (
        <div className="plan-ed-msg">
          <Message>{msg}</Message>
        </div>
      )}

      <aside className="plan-left" aria-label="Werkzeuge">
        <MiniPlan data={data} wallId={wallId} onPick={onSelectWall} />
        <p className="muted-s">Im kleinen Grundriss eine Wand anklicken, um sie anzuzeigen. Der Pfeil zeigt Ihre Blickrichtung.</p>
        <label className="plan-check">
          <input type="checkbox" checked={showZones} onChange={(e) => toggleZones(e.target.checked)} /> Installationszonen anzeigen
        </label>
        <div className="plan-tools plan-wv-tools">
          <button className={tool === "symbol" ? "on" : undefined} onClick={() => setTool("symbol")}>
            Symbol setzen
          </button>
          <button
            className={tool === "cable" ? "on" : undefined}
            onClick={() => {
              setTool("cable");
              setSel(null);
            }}
          >
            Leitung zeichnen
          </button>
          <button className={tool === "select" ? "on" : undefined} onClick={() => setTool("select")}>
            Auswählen
          </button>
        </div>
        {tool === "cable" ? (
          <>
            <p className="muted-s">Klicken Sie nacheinander die Punkte der Leitung an. Die Leitung läuft waagerecht oder senkrecht und rastet an den Installationszonen und Symbolen ein. Mit Enter oder Doppelklick ist sie fertig.</p>
            <label>
              Leitungsart
              <select value={cableType} onChange={(e) => setCableType(e.target.value)}>
                {CABLE_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {draft.length > 0 && (
              <div className="plan-sel-actions">
                <button className="dbtn small" onClick={finishCable} disabled={draft.length < 2}>
                  Leitung fertig
                </button>
                <button className="dbtn ghost small" onClick={cancelDraft}>
                  Abbrechen
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="muted-s">Symbol wählen, dann in die Wand klicken. Die Höhe rastet an den Installationszonen ein.</p>
            <div className="plan-cats" role="tablist">
              {SYMBOL_CATEGORIES.map((c) => (
                <button key={c} role="tab" aria-selected={category === c} className={category === c ? "on" : undefined} onClick={() => setCategory(c)}>
                  {c}
                </button>
              ))}
            </div>
            <ul className="plan-symbols">
              {SYMBOLS.filter((s) => s.category === category).map((s) => (
                <li key={s.id}>
                  <button
                    className={tool === "symbol" && placing === s.id ? "on" : undefined}
                    onClick={() => {
                      setPlacing(s.id);
                      setTool("symbol");
                      setSel(null);
                    }}
                  >
                    <SymbolIcon id={s.id} />
                    <span>{s.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>

      <div className="plan-canvas">
        <svg
          ref={svgRef}
          className={`plan-svg ${tool === "select" ? "tool-select" : "tool-symbol"}`}
          width="100%"
          height="100%"
          viewBox={`${-pad - 80} ${-H - pad} ${vbW} ${vbH}`}
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={() => setCursor(null)}
          onDoubleClick={() => tool === "cable" && draft.length >= 2 && finishCable()}
        >
          <rect x={-pad - 80} y={-H - pad} width={vbW} height={vbH} fill={PAPER} />
          <rect x={0} y={-H} width={inner} height={H} fill="#f1f3f7" stroke="#8f929a" strokeWidth={1.5} />
          {/* senkrechte Zonen */}
          {showZones && vzones.map((z, i) => (
            <rect key={i} x={z[0]} y={-H} width={Math.max(0, z[1] - z[0])} height={H} fill="#2f6fd0" fillOpacity={0.07} />
          ))}
          {/* waagerechte Zonen: Band ±15 cm um die Mitte */}
          {showZones && zones.map((z) => (
            <g key={z.h}>
              <rect x={0} y={-Math.min(H, z.h + 15)} width={inner} height={Math.min(H, z.h + 15) - Math.max(0, z.h - 15)} fill="#2f6fd0" fillOpacity={0.09} />
              <line x1={-6} y1={-z.h} x2={inner + 6} y2={-z.h} stroke="#2f6fd0" strokeOpacity={0.55} strokeWidth={1} strokeDasharray="8 5" />
              <text x={-8} y={-z.h + 1} fontSize={9} textAnchor="end" dominantBaseline="central" fill="#2f6fd0" fontFamily={FONT}>
                {z.label}
              </text>
            </g>
          ))}
          <line x1={-pad} y1={0} x2={inner + pad} y2={0} stroke={INK} strokeWidth={2} />
          <text x={inner + 8} y={-H + 1} fontSize={9} dominantBaseline="central" fill="#5a606a" fontFamily={FONT}>
            Decke {fmtCm(H)} cm
          </text>
          <text x={0} y={16} fontSize={10} fill="#5a606a" fontFamily={FONT}>
            links
          </text>
          <text x={inner} y={16} fontSize={10} textAnchor="end" fill="#5a606a" fontFamily={FONT}>
            rechts
          </text>
          {/* Fenster und Türen */}
          {openings.map((o) => {
            const def = OPENING_DEFAULTS[o.kind];
            const h = o.h ?? def.h;
            const sill = o.kind === "fenster" ? (o.sill ?? def.sill) : 0;
            const e = openingEdges(data, o);
            const x = drag?.k === "opening" && drag.id === o.id ? drag.x - o.w / 2 : (e?.left ?? 0);
            const on = sel?.k === "opening" && sel.id === o.id;
            return (
              <g key={o.id}>
                <rect x={x} y={-(sill + h)} width={o.w} height={h} fill="#e4ecf8" stroke={on ? HILITE : INK} strokeWidth={on ? 2.2 : 1.4} />
                {o.kind === "fenster" && <line x1={x + o.w / 2} y1={-(sill + h)} x2={x + o.w / 2} y2={-sill} stroke={INK} strokeWidth={0.8} />}
                <text x={x + o.w / 2} y={-(sill + h) - 5} fontSize={9} textAnchor="middle" fill="#5a606a" fontFamily={FONT}>
                  {OPENING_KINDS[o.kind].name} {fmtCm(o.w)} × {fmtCm(h)}
                  {sill > 0 ? ` · Brüstung ${fmtCm(sill)}` : ""}
                </text>
              </g>
            );
          })}
          {/* Maße zur Ecke für die gewählte Öffnung */}
          {selOpening && edges && (
            <g fontFamily={FONT} fontSize={9} fill={HILITE} stroke={HILITE} pointerEvents="none">
              <line x1={0} y1={dimY} x2={edges.left} y2={dimY} strokeWidth={1} />
              <line x1={0} y1={dimY - 4} x2={0} y2={dimY + 4} strokeWidth={1} />
              <line x1={edges.left} y1={dimY - 4} x2={edges.left} y2={dimY + 4} strokeWidth={1} />
              <text x={edges.left / 2} y={dimY + 12} textAnchor="middle" stroke="none">
                {fmtCm(edges.left)} cm
              </text>
              <line x1={edges.left + selOpening.w} y1={dimY} x2={inner} y2={dimY} strokeWidth={1} />
              <line x1={edges.left + selOpening.w} y1={dimY - 4} x2={edges.left + selOpening.w} y2={dimY + 4} strokeWidth={1} />
              <line x1={inner} y1={dimY - 4} x2={inner} y2={dimY + 4} strokeWidth={1} />
              <text x={(edges.left + selOpening.w + inner) / 2} y={dimY + 12} textAnchor="middle" stroke="none">
                {fmtCm(edges.right)} cm
              </text>
            </g>
          )}
          {/* Leitungen */}
          {cables.map((c) => {
            const v = c.pts.map(([x, h]) => [fx(x - a0), h] as Pt);
            const on = sel?.k === "cable" && sel.id === c.id;
            const mid = v[Math.floor(v.length / 2)]!;
            return (
              <g key={c.id}>
                <polyline points={v.map((p) => `${p[0]},${-p[1]}`).join(" ")} fill="none" stroke={on ? HILITE : "#c0392b"} strokeWidth={on ? 3 : 2.2} strokeLinejoin="round" strokeLinecap="round" />
                <text x={mid[0] + 4} y={-mid[1] - 4} fontSize={8} fill={on ? HILITE : "#c0392b"} fontFamily={FONT}>
                  {c.type}
                </text>
              </g>
            );
          })}
          {draft.length > 0 && (
            <polyline
              points={[...draft, ...(cursor ? [[cursor.x, cursor.h] as Pt] : [])].map((p) => `${p[0]},${-p[1]}`).join(" ")}
              fill="none"
              stroke="#c0392b"
              strokeWidth={2.2}
              strokeDasharray="6 4"
              pointerEvents="none"
            />
          )}
          {/* Symbole */}
          {mine.map((s) => {
            const def = symbolDef(s.type);
            if (!def) return null;
            const at = drag?.k === "symbol" && drag.id === s.id ? drag : { x: fx(s.pos! - a0), h: s.h ?? 30 };
            const sz = size(symbolK(s));
            const on = sel?.k === "symbol" && sel.id === s.id;
            return (
              <g key={s.id}>
                <g transform={`translate(${at.x} ${-at.h}) scale(${sz / 20})`}>
                  <Glyph prims={def.prims} sw={1.3} />
                </g>
                <text x={at.x + sz * 0.6} y={-at.h - sz * 0.5} fontSize={8} fill={INK} fontFamily={FONT}>
                  {[s.label, `${fmtCm(at.h)} cm`].filter(Boolean).join(" · ")}
                </text>
                {on && <rect x={at.x - sz * 0.65} y={-at.h - sz * 0.65} width={sz * 1.3} height={sz * 1.3} fill="none" stroke={HILITE} strokeWidth={1.6} strokeDasharray="5 3" />}
              </g>
            );
          })}
          {selSymbol && (
            <g fontFamily={FONT} fontSize={8} fill={HILITE} stroke={HILITE} pointerEvents="none" strokeDasharray="3 3" strokeWidth={0.8}>
              <line x1={0} y1={-(selSymbol.h ?? 30)} x2={(drag?.k === "symbol" ? drag.x : fx(selSymbol.pos! - a0)) - sizeSel * 0.65} y2={-(selSymbol.h ?? 30)} />
              <line x1={drag?.k === "symbol" ? drag.x : fx(selSymbol.pos! - a0)} y1={0} x2={drag?.k === "symbol" ? drag.x : fx(selSymbol.pos! - a0)} y2={-(selSymbol.h ?? 30) + sizeSel * 0.65} />
            </g>
          )}
          {tool === "symbol" && cursor && !drag && symbolDef(placing) && (
            <g opacity={0.5} transform={`translate(${cursor.x} ${-cursor.h}) scale(${size(1) / 20})`} pointerEvents="none">
              <Glyph prims={symbolDef(placing)!.prims} sw={1.3} />
            </g>
          )}
          {cursor && (
            <text x={cursor.x + 14} y={-cursor.h + 20} fontSize={9} fill={HILITE} fontWeight={700} fontFamily={FONT} pointerEvents="none">
              {fmtCm(cursor.x)} cm von links · {fmtCm(cursor.h)} cm hoch
            </text>
          )}
        </svg>
        <p className="plan-hint">
          Die Wand ist von innen gesehen, links ist links, wenn Sie im Raum vor der Wand stehen. Anklicken und ziehen verschiebt ein Teil, Entf löscht es, Esc beendet das Werkzeug. Die blau hinterlegten Streifen sind die Installationszonen.
        </p>
      </div>

      <aside className="plan-right" aria-label="Eigenschaften">
        <section className="plan-card2">
          <h3>Auswahl</h3>
          {!sel && <p className="muted-s">Klicken Sie ein Teil, eine Leitung oder (im Werkzeug „Auswählen“) ein Fenster bzw. eine Tür an.</p>}
          {selSymbol && (
            <>
              <p className="plan-sel-name">{symbolDef(selSymbol.type)?.name}</p>
              <label>
                Bezeichnung (z. B. Q1, S2)
                <input value={selSymbol.label ?? ""} maxLength={80} onChange={(e) => patchSymbol(selSymbol.id, { label: e.target.value })} />
              </label>
              <Field label="Abstand von links (cm)" value={fx(selSymbol.pos! - a0)} max={inner} setMsg={setMsg} onCommit={(v) => patchSymbol(selSymbol.id, { pos: fx(v) + a0 })} />
              <Field label="Höhe über Fußboden (cm)" value={selSymbol.h ?? 30} max={H} setMsg={setMsg} onCommit={(v) => patchSymbol(selSymbol.id, { h: v })} />
              <Field label="Größe (%)" value={Math.round(symbolK(selSymbol) * 100)} min={30} max={400} setMsg={setMsg} onCommit={(v) => patchSymbol(selSymbol.id, { k: v / 100 })} />
              <div className="plan-sel-actions">
                <button className="dbtn ghost small danger" onClick={removeSelected}>
                  Löschen
                </button>
              </div>
            </>
          )}
          {selOpening && edges && (
            <>
              <p className="plan-sel-name">{OPENING_KINDS[selOpening.kind].name}</p>
              <Field label="Abstand zur linken Ecke (cm)" value={edges.left} max={inner} setMsg={setMsg} onCommit={(v) => onCommit(setOpeningLeft(data, selOpening.id, v))} />
              <Field label="Abstand zur rechten Ecke (cm)" value={edges.right} max={inner} setMsg={setMsg} onCommit={(v) => onCommit(setOpeningLeft(data, selOpening.id, inner - selOpening.w - v))} />
              <Field label="Breite (cm)" value={selOpening.w} min={20} max={1000} setMsg={setMsg} onCommit={(v) => patchOpening(selOpening.id, { w: v })} />
              <Field label="Höhe der Öffnung (cm)" value={selOpening.h ?? OPENING_DEFAULTS[selOpening.kind].h} min={20} max={600} setMsg={setMsg} onCommit={(v) => patchOpening(selOpening.id, { h: v })} />
              {selOpening.kind === "fenster" && (
                <Field label="Brüstungshöhe (cm)" value={selOpening.sill ?? OPENING_DEFAULTS.fenster.sill} max={600} setMsg={setMsg} onCommit={(v) => patchOpening(selOpening.id, { sill: v })} />
              )}
              <p className="muted-s">Alle Abstände gelten zwischen den Wandflächen (lichte Maße). Sie können das Teil auch mit der Maus verschieben.</p>
            </>
          )}
          {selCable && (
            <>
              <p className="plan-sel-name">Leitung · ca. {formatMeters(cableLen(selCable as { pts: Pt[] }))}</p>
              <label>
                Leitungsart
                <select
                  value={selCable.type}
                  onChange={(e) => onCommit({ ...data, cables: data.cables.map((c) => (c.id === selCable.id ? { ...c, type: e.target.value } : c)) })}
                >
                  {[...new Set([...CABLE_TYPES, selCable.type])].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              <div className="plan-sel-actions">
                <button className="dbtn ghost small danger" onClick={removeSelected}>
                  Löschen
                </button>
              </div>
            </>
          )}
        </section>

        <section className="plan-card2">
          <h3>Diese Wand</h3>
          <Field
            label={room ? `Raumhöhe von „${room.name || "Raum"}“ (cm)` : "Raumhöhe (cm)"}
            value={H}
            min={150}
            max={1000}
            setMsg={setMsg}
            onCommit={(v) =>
              onCommit(room ? { ...data, rooms: data.rooms.map((r) => (r.id === room.id ? { ...r, height: v } : r)) } : { ...data, ceiling: v })
            }
          />
          <p className="muted-s">Die Zonen: {zones.map((z) => z.label).join(", ")}. Streifen von ±15 cm um die Mitte, senkrecht 15 cm neben Ecken, Fenstern und Türen.</p>
        </section>

        <section className="plan-card2">
          <h3>Reihe anlegen</h3>
          <p className="muted-s">Mehrere gleiche Teile gleichmäßig auf der Wand verteilen. Stellen in Türen und Fenstern bleiben frei.</p>
          <label>
            Symbol
            <select value={series.type} onChange={(e) => setSeries({ ...series, type: e.target.value })}>
              {SYMBOLS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <Field label="Anzahl" value={series.count} min={1} max={30} setMsg={setMsg} onCommit={(v) => setSeries({ ...series, count: Math.round(v) })} />
          <label>
            Höhe
            <select
              value={zones.some((z) => z.h === series.h) ? series.h : "andere"}
              onChange={(e) => e.target.value !== "andere" && setSeries({ ...series, h: Number(e.target.value) })}
            >
              {zones.map((z) => (
                <option key={z.h} value={z.h}>
                  {z.label === `${z.h} cm` ? z.label : `${z.h} cm (${z.label})`}
                </option>
              ))}
              {!zones.some((z) => z.h === series.h) && <option value="andere">{series.h} cm</option>}
            </select>
          </label>
          <Field label="Oder andere Höhe (cm)" value={series.h} max={H} setMsg={setMsg} onCommit={(v) => setSeries({ ...series, h: v })} />
          <Field label="Abstand zum Rand (cm)" value={series.margin} max={inner} setMsg={setMsg} onCommit={(v) => setSeries({ ...series, margin: v })} />
          <button className="dbtn small" onClick={addSeries}>
            Anlegen
          </button>
        </section>

        <section className="plan-card2">
          <h3>Auf dieser Wand</h3>
          {mine.length === 0 && cables.length === 0 && <p className="muted-s">Noch nichts. Wählen Sie links ein Symbol und klicken Sie in die Wand.</p>}
          <ul className="plan-legend">
            {[...mine]
              .sort((a, b) => fx(a.pos! - a0) - fx(b.pos! - a0))
              .map((s) => (
                <li key={s.id}>
                  <SymbolIcon id={s.type} size={22} />
                  <button className="plan-linkish" onClick={() => setSel({ k: "symbol", id: s.id })}>
                    {symbolDef(s.type)?.name} {s.label ? `(${s.label})` : ""}
                  </button>
                  <b>
                    {fmtCm(fx(s.pos! - a0))} / {fmtCm(s.h ?? 30)}
                  </b>
                </li>
              ))}
            {cables.map((c) => (
              <li key={c.id}>
                <button className="plan-linkish" onClick={() => setSel({ k: "cable", id: c.id })}>
                  Leitung {c.type}
                </button>
                <b>{formatMeters(cableLen(c as { pts: Pt[] }))}</b>
              </li>
            ))}
          </ul>
          {mine.length > 0 && <p className="muted-s">Angabe: Abstand von links / Höhe in cm.</p>}
        </section>
      </aside>
    </div>,
    document.querySelector(".hv-app") ?? document.body,
  );
}

/* ---------- Raum mit Maßen ---------- */

export function RoomDialog(props: { onClose: () => void; defaultHeight: number; onCreate: (o: { name: string; w: number; h: number; t: number; basis: RoomBasis; height: number }) => void }) {
  const [name, setName] = useState("Wohnzimmer");
  const [w, setW] = useState("5,00");
  const [h, setH] = useState("4,00");
  const [t, setT] = useState<number>(WALL_PRESETS[2].t);
  const [basis, setBasis] = useState<RoomBasis>("innen");
  const [ceil, setCeil] = useState(String(props.defaultHeight));
  const [error, setError] = useState("");

  function submit() {
    const wc = parseLength(w);
    const hc = parseLength(h);
    if (wc === null || hc === null) return setError("Bitte Breite und Länge eingeben, z. B. 5,00 (Meter) oder 500 (Zentimeter).");
    if (wc < 100 || hc < 100) return setError("Ein Raum sollte mindestens 1 m × 1 m groß sein.");
    const cc = parseLength(ceil);
    if (cc === null || cc < 150 || cc > 1000) return setError("Bitte die Deckenhöhe eingeben, z. B. 2,50 (Meter) oder 250 (Zentimeter). Sie liegt meist zwischen 2,25 m und 3 m.");
    props.onCreate({ name: name.trim() || "Raum", w: wc, h: hc, t, basis, height: cc });
  }
  return (
    <Sheet title="Raum mit Maßen anlegen" onClose={props.onClose}>
      <form
        className="plan-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Message>{error}</Message>
        <label>
          Name des Raums
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
        </label>
        <label>
          Breite (von links nach rechts)
          <input value={w} onChange={(e) => setW(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          Länge (von oben nach unten)
          <input value={h} onChange={(e) => setH(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          Deckenhöhe (Raumhöhe, fertiger Fußboden bis Decke)
          <input value={ceil} onChange={(e) => setCeil(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          Die Maße gelten als
          <select value={basis} onChange={(e) => setBasis(e.target.value as RoomBasis)}>
            <option value="innen">Innenmaß (lichte Maße, zwischen den Wänden)</option>
            <option value="aussen">Außenmaß (außen an den Wänden)</option>
            <option value="mitte">Maß bis Wandmitte</option>
          </select>
        </label>
        <label>
          Wandstärke
          <select value={t} onChange={(e) => setT(Number(e.target.value))}>
            {WALL_PRESETS.map((p) => (
              <option key={p.t} value={p.t}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <p className="muted-s">Zahlen ohne Komma sind Zentimeter (500), mit Komma Meter (5,00). Alle Maße lassen sich danach ändern: Wand anklicken und die Länge eintragen.</p>
        <button className="dbtn" type="submit">
          Raum anlegen
        </button>
      </form>
    </Sheet>
  );
}

/* ---------- Start: Blatt und erster Raum ---------- */

export type StartResult = {
  sheet: PlanSheet;
  portrait: boolean;
  scale: number;
  room: { name: string; w: number; h: number; t: number; basis: RoomBasis; height: number } | null;
};

export function StartDialog(props: { defaultHeight: number; onClose: () => void; onDone: (r: StartResult) => void }) {
  const [sheet, setSheet] = useState<PlanSheet>("A3");
  const [portrait, setPortrait] = useState(false);
  const [scale, setScale] = useState(50);
  const [withRoom, setWithRoom] = useState(true);
  const [name, setName] = useState("Wohnzimmer");
  const [w, setW] = useState("5,00");
  const [h, setH] = useState("4,00");
  const [t, setT] = useState<number>(WALL_PRESETS[2].t);
  const [basis, setBasis] = useState<RoomBasis>("innen");
  const [ceil, setCeil] = useState(String(props.defaultHeight));
  const [error, setError] = useState("");

  function submit() {
    if (!withRoom) return props.onDone({ sheet, portrait, scale, room: null });
    const wc = parseLength(w);
    const hc = parseLength(h);
    if (wc === null || hc === null) return setError("Bitte Breite und Länge eingeben, z. B. 5,00 (Meter) oder 500 (Zentimeter).");
    if (wc < 100 || hc < 100) return setError("Ein Raum sollte mindestens 1 m × 1 m groß sein.");
    const cc = parseLength(ceil);
    if (cc === null || cc < 150 || cc > 1000) return setError("Bitte die Deckenhöhe eingeben, z. B. 2,50 (Meter) oder 250 (Zentimeter).");
    const [sw, sh] = PLAN_SHEETS[sheet];
    const need = { w: ((wc + 2 * t) * 10) / scale, h: ((hc + 2 * t) * 10) / scale };
    const paper = { w: (portrait ? sh : sw) - 40, h: (portrait ? sw : sh) - 70 };
    if (need.w > paper.w || need.h > paper.h) return setError(`Der Raum passt bei 1:${scale} nicht auf ${sheet}. Bitte ein größeres Blatt oder einen kleineren Maßstab (z. B. 1:${scale * 2}) wählen.`);
    props.onDone({ sheet, portrait, scale, room: { name: name.trim() || "Raum", w: wc, h: hc, t, basis, height: cc } });
  }
  return (
    <Sheet title="Plan einrichten" onClose={props.onClose}>
      <form
        className="plan-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Message>{error}</Message>
        <h4 className="plan-step">1. Blatt</h4>
        <label>
          Blattgröße
          <select value={sheet} onChange={(e) => setSheet(e.target.value as PlanSheet)}>
            {(Object.keys(PLAN_SHEETS) as PlanSheet[]).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ausrichtung
          <select value={portrait ? "hoch" : "quer"} onChange={(e) => setPortrait(e.target.value === "hoch")}>
            <option value="quer">Querformat</option>
            <option value="hoch">Hochformat</option>
          </select>
        </label>
        <label>
          Maßstab
          <select value={scale} onChange={(e) => setScale(Number(e.target.value))}>
            {PLAN_SCALES.map((s) => (
              <option key={s} value={s}>
                1:{s}
              </option>
            ))}
          </select>
        </label>
        <h4 className="plan-step">2. Erster Raum</h4>
        <label className="plan-check">
          <input type="checkbox" checked={withRoom} onChange={(e) => setWithRoom(e.target.checked)} /> Gleich einen Raum mit Maßen anlegen
        </label>
        {withRoom && (
          <>
            <label>
              Name des Raums
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />
            </label>
            <label>
              Breite (von links nach rechts)
              <input value={w} onChange={(e) => setW(e.target.value)} inputMode="decimal" />
            </label>
            <label>
              Länge (von oben nach unten)
              <input value={h} onChange={(e) => setH(e.target.value)} inputMode="decimal" />
            </label>
            <label>
              Deckenhöhe (fertiger Fußboden bis Decke)
              <input value={ceil} onChange={(e) => setCeil(e.target.value)} inputMode="decimal" />
            </label>
            <label>
              Die Maße gelten als
              <select value={basis} onChange={(e) => setBasis(e.target.value as RoomBasis)}>
                <option value="innen">Innenmaß (lichte Maße, zwischen den Wänden)</option>
                <option value="aussen">Außenmaß (außen an den Wänden)</option>
                <option value="mitte">Maß bis Wandmitte</option>
              </select>
            </label>
            <label>
              Wandstärke
              <select value={t} onChange={(e) => setT(Number(e.target.value))}>
                {WALL_PRESETS.map((p) => (
                  <option key={p.t} value={p.t}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted-s">Zahlen ohne Komma sind Zentimeter (500), mit Komma Meter (5,00). Alles lässt sich später ändern.</p>
          </>
        )}
        <button className="dbtn" type="submit">
          Los geht’s
        </button>
      </form>
    </Sheet>
  );
}
